import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react';

import { ChatMessage, ChatWidgetContextType, ChatWidgetState } from '@/types/chatWidget';

// Initial state
const initialState: ChatWidgetState = {
  isOpen: false,
  messages: [],
  unreadCount: 0,
  session_id: undefined,
  documentId: undefined,
};

// Action types
type Action =
  | { type: 'TOGGLE_CHAT' }
  | { type: 'OPEN_CHAT' }
  | { type: 'CLOSE_CHAT' }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'MARK_ALL_READ' }
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'SET_DOCUMENT_ID'; payload: string };

// Reducer
const chatReducer = (state: ChatWidgetState, action: Action): ChatWidgetState => {
  switch (action.type) {
  case 'TOGGLE_CHAT':
    return {
      ...state,
      isOpen: !state.isOpen,
      unreadCount: state.isOpen ? state.unreadCount : 0,
    };
  case 'OPEN_CHAT':
    return {
      ...state,
      isOpen: true,
      unreadCount: 0,
    };
  case 'CLOSE_CHAT':
    return {
      ...state,
      isOpen: false,
    };
  case 'ADD_MESSAGE':
    return {
      ...state,
      messages: [...state.messages, action.payload],
      unreadCount: state.isOpen ? state.unreadCount : state.unreadCount + 1,
    };
  case 'MARK_ALL_READ':
    return {
      ...state,
      unreadCount: 0,
    };
  case 'SET_SESSION_ID':
    return {
      ...state,
      session_id: action.payload,
    };
  case 'SET_DOCUMENT_ID':
    return {
      ...state,
      documentId: action.payload,
    };
  default:
    return state;
  }
};

// Create context
const ChatWidgetContext = createContext<ChatWidgetContextType | undefined>(undefined);

// Create delay function
const delay = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Context provider
interface ChatWidgetProviderProps extends PropsWithChildren {
  documentId?: string;
}

export const ChatWidgetProvider: React.FC<ChatWidgetProviderProps> = ({ children, documentId }) => {
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialState,
    documentId,
  });

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_CHAT' });
  }, []);

  const closeChat = useCallback(() => {
    dispatch({ type: 'CLOSE_CHAT' });
  }, []);

  const openChat = useCallback(() => {
    dispatch({ type: 'OPEN_CHAT' });
  }, []);

  const setSessionId = useCallback((sessionId: string) => {
    dispatch({ type: 'SET_SESSION_ID', payload: sessionId });
  }, []);

  const setDocumentId = useCallback((docId: string) => {
    dispatch({ type: 'SET_DOCUMENT_ID', payload: docId });
  }, []);

  // Update document ID if prop changes
  useEffect(() => {
    if (documentId) {
      setDocumentId(documentId);
    }
  }, [documentId, setDocumentId]);

  const sendMessage = useCallback(
    async (
      content: string,
      sender: 'user' | 'system' = 'user',
      type: 'general' | 'documents' = 'general'
    ) => {
      const messageId =
        state.messages.length > 0
          ? Math.max(...state.messages.map((msg: ChatMessage) => msg.id)) + 1
          : 1;

      const newMessage: ChatMessage = {
        id: messageId,
        content,
        sender,
        timestamp: new Date(),
        status: 'sending',
        type,
      };

      dispatch({ type: 'ADD_MESSAGE', payload: newMessage });

      // Simulate API call delay - only for user messages
      if (sender === 'user') {
        await delay(1000);

        // Simulate response - only if not using real API responses
        // Remove this block when using real API responses
        // const systemResponse: ChatMessage = {
        //   id: messageId + 1,
        //   content: 'Thanks for your message! Our team will get back to you shortly.',
        //   sender: 'system',
        //   timestamp: new Date(),
        //   status: 'sent',
        //   type,
        // };
        //
        // dispatch({ type: 'ADD_MESSAGE', payload: systemResponse });
      }
    },
    [state.messages]
  );

  const markAllAsRead = useCallback(() => {
    dispatch({ type: 'MARK_ALL_READ' });
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      state,
      toggleChat,
      closeChat,
      openChat,
      sendMessage,
      markAllAsRead,
      setSessionId,
      setDocumentId,
    }),
    [
      state,
      toggleChat,
      closeChat,
      openChat,
      sendMessage,
      markAllAsRead,
      setSessionId,
      setDocumentId,
    ]
  );

  return <ChatWidgetContext.Provider value={contextValue}>{children}</ChatWidgetContext.Provider>;
};

// Custom hook for using the chat context
export const useChatWidget = (): ChatWidgetContextType => {
  const context = useContext(ChatWidgetContext);
  if (!context) {
    throw new Error('useChatWidget must be used within a ChatWidgetProvider');
  }
  return context;
};
